import { CollapsiblePanel } from '@/app/components/CollapsiblePanel'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { Headline2, Headline3 } from '@/app/components/Typography'
import { useMyUser } from '@/app/hooks/data-hooks/useMyUser'
import { WEBSITE_LINKS } from '@/app/lib/constants'
import { ICourse } from '@/types/course'
import { IFinancialProductSponsor } from '@/types/financial-product-sponsor'
import { FinancialTermType } from '@/types/financial-term'
import { Skeleton } from '@mui/material'
import { useTranslations } from 'next-intl'
import Link from 'next/link'

type CourseFAQProps = {
  onGetInTouch?: () => void
  course: Pick<ICourse, 'financialTerms' | 'name' | 'educationProvider'>
} & React.HTMLAttributes<HTMLDivElement>

export function CourseFAQ({ onGetInTouch, course, ...props }: CourseFAQProps) {
  const t = useTranslations('CoursePage')
  const {
    data: { data: { firstName, lastName, email } = {} } = {},
    isLoading: isLoadingMyUser,
  } = useMyUser()

  const [financialTerm] = course.financialTerms ?? []
  const sponsorName = (
    financialTerm?.financialProductSponsor as IFinancialProductSponsor
  )?.name
  const isIsaSponsoredCourse = financialTerm?.type === FinancialTermType.Isa

  const showChatBotForm = () => {
    const emailBody = `Hello,
    I would like to know more about the course ${course.name} (${course.educationProvider.name}) (${window.location.href}).`

    try {
      window.Beacon('prefill', {
        name: [firstName, lastName].filter(Boolean).join(' '),
        email,
        subject: `Course information about ${course.name} (${course.educationProvider.name})`,
        text: emailBody,
      })
      window.Beacon('navigate', '/ask/message/')
      window.Beacon('open')
    } catch (e) {
      // send an e-mail to support@mydra.io
      // prefill the email with the following content
      window.open(
        `mailto:support@mydra.io?subject=Course information about ${course.name} (${course.educationProvider.name})&body=${encodeURIComponent(emailBody)}`,
        '_blank',
        'noopener,noreferrer'
      )
    }
  }

  const faqItems = [
    {
      question: t('faq.how_can_i_learn_more_about_this_course'),
      answer: t.rich('faq.how_can_i_learn_more_about_this_course_answer', {
        LinkChat: (children) => (
          <Link
            href="#chat"
            className="underline"
            onClick={(e) => {
              e.preventDefault()
              showChatBotForm()
            }}
          >
            {children}
          </Link>
        ),
        LinkEmail: (children) => (
          <Link
            target="_blank"
            href="mailto:applications@mydra.io"
            className="underline"
          >
            {children}
          </Link>
        ),
      }),
    },
    {
      question: t('faq.what_are_the_payment_options_available'),
      answer: t.rich(
        isIsaSponsoredCourse && !!sponsorName
          ? 'faq.what_are_the_payment_options_available_answer_isa_sponsored_course'
          : 'faq.what_are_the_payment_options_available_answer',
        {
          LinkTerms: (children) => (
            <Link
              target="_blank"
              href={WEBSITE_LINKS.termsAndConditions}
              className="underline"
            >
              {children}
            </Link>
          ),
          sponsorName,
        }
      ),
    },
    {
      question: t('faq.how_does_mydra_vet_top_courses'),
      answer: t('faq.how_does_mydra_vet_top_courses_answer'),
    },
  ]

  return isLoadingMyUser ? (
    <Skeleton className="h-10 w-full" />
  ) : (
    <div
      {...props}
      className={`flex flex-col gap-6 px-8 py-8 md:px-0 md:py-24 ${props.className}`}
    >
      <Headline2 className="text-center">{t('faq.title')}</Headline2>
      <div className="flex flex-col gap-6 md:flex-row md:gap-16">
        <div className="flex flex-shrink-0 flex-col gap-4 md:basis-1/2">
          {faqItems.map(({ question, answer }, index) => (
            <CollapsiblePanel
              key={index}
              className={`rounded-none border-b border-l-0 border-r-0 border-t-0 !px-0 !pb-4 md:last:border-b-0`}
              noToggle
              header={
                <span className="font-title text-lg font-normal text-gray-900">
                  {question}
                </span>
              }
            >
              <span className="text-sm">{answer}</span>
            </CollapsiblePanel>
          ))}
        </div>
        <div className="flex w-full flex-col self-start rounded-2xl bg-mydra-violet p-4">
          <Headline3 className="md:text-lg">
            {t('faq.stillHaveQuestions')}
          </Headline3>
          <p className="!md:text-base mt-2 text-sm text-black">
            {t('faq.stillHaveQuestionsDescription')}
          </p>
          <PrimaryButton
            className="mt-4 rounded-lg"
            type="button"
            onClick={() => onGetInTouch?.()}
          >
            {t('faq.getInTouch')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  )
}
